import { useCallback } from "react";

import { Notification } from "@magnetic/notification";

import { Button } from "src/components/buttons";
import { HFlex } from "src/components/hFlex";

import "./versionUpdateBanner.scss";

interface VersionUpdateBannerProps {
  /** Whether the banner should be visible. Returns `null` when `false`. */
  readonly show: boolean;
  /** The version the user is currently running */
  readonly currentVersion: string;
  /** The version the server says the user should be running */
  readonly serverVersion: string;
  /** Callback when user clicks "Update Now" */
  readonly onAccept: () => void;
  /** Callback when user clicks "Later" */
  readonly onDismiss: () => void;
}

/**
 * Banner component that notifies user when a new UI version is available.
 * Displayed at the top of the app when server version differs from current.
 * Returns `null` when `show` is `false`.
 */
export const VersionUpdateBanner = ({
  show,
  onAccept,
  onDismiss
}: VersionUpdateBannerProps): JSX.Element | null => {
  const handleAccept = useCallback(() => {
    onAccept();
  }, [onAccept]);

  const handleDismiss = useCallback(() => {
    onDismiss();
  }, [onDismiss]);

  if (!show) return null;

  return (
    <div className="version-update-banner-container">
      <Notification
        className="version-update-notification"
        banner="product"
        status="warning"
        title="Update Nexus Hyperfabric"
      >
        <HFlex justify="flex-end" align="center" gap="md">
          <span style={{ flex: 1 }}>
            This update does not change any fabric or device configurations.
            Select <strong>Refresh</strong> to continue.
          </span>
          <HFlex gap="sm" shrink={false}>
            <Button
              name="update-later"
              kind="secondary"
              onClick={handleDismiss}
            >
              Remind later
            </Button>
            <Button name="update-now" kind="primary" onClick={handleAccept}>
              Refresh
            </Button>
          </HFlex>
        </HFlex>
      </Notification>
    </div>
  );
};

export default VersionUpdateBanner;
